import React, { HTMLAttributes } from 'react';
import { classNames } from '@/utils/classNames';

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  dismissible = false,
  onDismiss,
  icon,
  className,
  children,
  ...props
}) => {
  const baseClasses = 'rounded-md p-4 fade-in';

  const variantClasses = {
    info: 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    success: 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800',
    warning: 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
    error: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800',
  };

  const textColorClasses = {
    info: 'text-blue-700 dark:text-blue-200',
    success: 'text-green-700 dark:text-green-200',
    warning: 'text-yellow-700 dark:text-yellow-200',
    error: 'text-red-700 dark:text-red-200',
  };

  const iconColorClasses = {
    info: 'text-blue-400 dark:text-blue-300',
    success: 'text-green-400 dark:text-green-300',
    warning: 'text-yellow-400 dark:text-yellow-300',
    error: 'text-red-400 dark:text-red-300',
  };

  const DefaultIcon = () => {
    const iconProps = {
      className: classNames('h-5 w-5', iconColorClasses[variant]),
      fill: 'currentColor',
      viewBox: '0 0 20 20',
    };

    switch (variant) {
      case 'success':
        return (
          <svg {...iconProps}>
            <path
              fillRule='evenodd'
              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
              clipRule='evenodd'
            />
          </svg>
        );
      case 'warning':
        return (
          <svg {...iconProps}>
            <path
              fillRule='evenodd'
              d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
              clipRule='evenodd'
            />
          </svg>
        );
      case 'error':
        return (
          <svg {...iconProps}>
            <path
              fillRule='evenodd'
              d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
              clipRule='evenodd'
            />
          </svg>
        );
      default: // info
        return (
          <svg {...iconProps}>
            <path
              fillRule='evenodd'
              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
              clipRule='evenodd'
            />
          </svg>
        );
    }
  };

  const alertClasses = classNames(baseClasses, variantClasses[variant], className);

  return (
    <div className={alertClasses} role='alert' {...props}>
      <div className='flex'>
        <div className='flex-shrink-0'>{icon || <DefaultIcon />}</div>
        <div className='ml-3 flex-1'>
          {title && (
            <h3 className={classNames('text-sm font-medium', textColorClasses[variant])}>
              {title}
            </h3>
          )}
          <div className={classNames('text-sm', textColorClasses[variant], title && 'mt-2')}>
            {children}
          </div>
        </div>
        {dismissible && onDismiss && (
          <div className='ml-auto pl-3'>
            <div className='-mx-1.5 -my-1.5'>
              <button
                type='button'
                onClick={onDismiss}
                className={classNames(
                  'inline-flex rounded-md p-1.5 transition-colors touch-target',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2',
                  iconColorClasses[variant],
                  'hover:bg-current/20 focus:bg-current/30'
                )}
                aria-label='Dismiss alert'
              >
                <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;
